import { t } from 'i18next'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { ServerDomainInfo } from '@/api/type-server'
import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react'
import { EmptyView, Trans } from '@/components'
import { BILL_TYPE_DISPLAY } from '@/models/bill'
import { Instance } from '@/models/instance'
import { formatPrice } from '@/utils/formatter'

interface Props {
  instances: Instance[]
  domainInfos: ServerDomainInfo[]
}

export function CostRankingTable({ instances, domainInfos }: Props) {
  // Sort by cost descending and take top 5
  const topInstances = useMemo(
    () => [...instances].sort((a, b) => b.cost - a.cost).slice(0, 5),
    [instances],
  )
  return (
    <div className="bg-panel-white rounded-2xl p-6 border border-neutral-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="h5">
          <Trans i18nKey="teamHome.member.costRanking.title">Cost Ranking by Instance</Trans>
        </h2>
        <Link
          to="/team/instances"
          className="flex items-center t-sm text-secondary bg-panel-gray-100 rounded-md pl-3 pr-2 py-2"
        >
          <Trans i18nKey="teamHome.member.costRanking.instancesLink">Instances</Trans>
          <ArrowRightIcon className="w-3 h-3" />
        </Link>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-panel-gray-100 text-secondary t-sm">
              <th className="text-left py-3 px-4 rounded-l-lg">
                <Trans i18nKey="teamHome.member.costRanking.table.rank">#</Trans>
              </th>
              <th className="text-left py-3 px-4">
                <Trans i18nKey="teamHome.member.costRanking.table.id">ID</Trans>
              </th>
              <th className="text-left py-3 px-4">
                <Trans i18nKey="teamHome.member.costRanking.table.name">Name</Trans>
              </th>
              <th className="text-left py-3 px-4">
                <Trans i18nKey="teamHome.member.costRanking.table.region">Region</Trans>
              </th>
              <th className="text-left py-3 px-4">
                <Trans i18nKey="teamHome.member.costRanking.table.status">Status</Trans>
              </th>
              <th className="text-left py-3 px-4">
                <Trans i18nKey="teamHome.member.costRanking.table.billingMethod">
                  Billing Method
                </Trans>
              </th>
              <th className="text-right py-3 px-4 rounded-r-lg">
                <Trans i18nKey="teamHome.member.costRanking.table.cost">Cost</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {topInstances.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyView title={t('common.emptyViewNoDataMsg', 'No Data')} />
                </td>
              </tr>
            ) : (
              topInstances.map((instance, index) => (
                <tr key={instance.resID} className="border-b border-neutral-100 t-sm">
                  <td className="py-4 px-4">{String(index + 1).padStart(2, '0')}</td>
                  <td className="py-4 px-4">{instance.displayID}</td>
                  <td className="py-4 px-4">{instance.displayTitle}</td>
                  <td className="py-4 px-4">
                    {domainInfos.find((info) => info.domain === instance.domain)?.displayName ||
                      `Domain-${instance.domain}` ||
                      '-'}
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          instance.isRunning ? 'bg-success' : 'bg-neutral-400'
                        }`}
                      />
                      <span className={`${instance.statusTextStyle}`}>{instance.statusText}</span>
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span>{BILL_TYPE_DISPLAY[instance.billType] || '-'}</span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    {formatPrice(instance.cost, false, '')}
                    {t('teamHome.member.costRanking.creditSuffix', ' Credit')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
